import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type { ConfluenceOnContentModified } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';

import CollaboratorsQuery, {
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery as CollaboratorsQueryType,
} from './__generated__/PageOwnershipByLineWithCollaboratorsDropdownContentQuery.graphql.ts';
import { getContentCollaboratorsQueryVariables } from './getContentCollaboratorsQueryVariables.ts';
import { useContributorsPolling } from './useContributorsPolling.ts';
import { useContributorsActivityRefresh } from './useContributorsActivityRefresh.ts';
import { useLiveContributorsActivity } from './useLiveContributorsActivity.ts';

type CollaboratorsQueryResult = {
	data?: CollaboratorsQueryType['response'];
	loading: boolean;
	error?: Error;
	refetch: () => Promise<unknown>;
};

const CollaboratorsQueryContext = createContext<CollaboratorsQueryResult | undefined>(undefined);
export const useCollaboratorsQueryResult = (): CollaboratorsQueryResult | undefined =>
	useContext(CollaboratorsQueryContext);

// Error boundaries retain their fallback component; context keeps children and retry current.
const FallbackContext = createContext<{
	children: React.ReactNode;
	refetch: () => Promise<unknown>;
}>({ children: null, refetch: async () => undefined });

const QueryErrorFallback = ({ error }: { error: Error }) => {
	const { children, refetch } = useContext(FallbackContext);
	const value = useMemo(() => ({ loading: false, error, refetch }), [error, refetch]);
	return (
		<CollaboratorsQueryContext.Provider value={value}>
			{children}
		</CollaboratorsQueryContext.Provider>
	);
};

type CollaboratorsQueryProviderProps = {
	children: React.ReactNode;
	isDropdownOpen: boolean;
};

// Mounted only on the dual-agent byline path. Refresh triggers remain active without demo polling.
export const CollaboratorsQueryProvider = (
	props: CollaboratorsQueryProviderProps,
): React.JSX.Element => {
	const isLivePage = useIsCurrentPageLive();

	return (
		<RefreshingCollaboratorsQueryProvider
			{...props}
			isLivePage={isLivePage}
			shouldIncludeJobTitle={expValEquals('cc_show_byline_job_title', 'isEnabled', true)}
		/>
	);
};

// The preloader warms the same Relay operation; both consumers read its shared result synchronously.
const RefreshingCollaboratorsQueryProvider = ({
	children,
	isDropdownOpen,
	isLivePage,
	shouldIncludeJobTitle,
}: CollaboratorsQueryProviderProps & {
	isLivePage: boolean;
	shouldIncludeJobTitle: boolean;
}): React.JSX.Element => {
	const environment = useRelayEnvironment();
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const variables = useMemo(
		() =>
			getContentCollaboratorsQueryVariables({
				cloudId,
				contentId: contentId || '',
				spaceKey: spaceKey || '',
				shouldIncludeJobTitle,
			}),
		[cloudId, contentId, spaceKey, shouldIncludeJobTitle],
	);
	const [retryCount, setRetryCount] = useState(0);
	const refetch = useCallback(
		() =>
			fetchQuery<CollaboratorsQueryType>(environment, CollaboratorsQuery, variables, {
				fetchPolicy: 'network-only',
			}).toPromise(),
		[environment, variables],
	);
	const retry = useCallback(async () => {
		await refetch();
		setRetryCount((count) => count + 1);
	}, [refetch]);
	const fallbackValue = useMemo(() => ({ children, refetch: retry }), [children, retry]);
	const loadingValue = useMemo(() => ({ loading: true, refetch: retry }), [retry]);

	if (!cloudId || !contentId || !spaceKey) {
		return <>{children}</>;
	}

	return (
		<FallbackContext.Provider value={fallbackValue}>
			<GenericErrorBoundary
				key={`${cloudId}:${contentId}:${spaceKey}:${retryCount}`}
				attribution={Attribution.PAGE_EXPERIENCES}
				renderOnError={QueryErrorFallback}
			>
				<RelaySuspense
					interactionName="collaborators-byline"
					fallback={
						<CollaboratorsQueryContext.Provider value={loadingValue}>
							{children}
						</CollaboratorsQueryContext.Provider>
					}
				>
					<QueriedCollaboratorsProvider
						variables={variables}
						refetch={refetch}
						fetchKey={retryCount}
						isDropdownOpen={isDropdownOpen}
						isLivePage={isLivePage}
					>
						{children}
					</QueriedCollaboratorsProvider>
				</RelaySuspense>
			</GenericErrorBoundary>
		</FallbackContext.Provider>
	);
};

const QueriedCollaboratorsProvider = ({
	children,
	variables,
	refetch,
	fetchKey,
	isDropdownOpen,
	isLivePage,
}: {
	children: React.ReactNode;
	variables: CollaboratorsQueryType['variables'];
	refetch: CollaboratorsQueryResult['refetch'];
	fetchKey: number;
	isDropdownOpen: boolean;
	isLivePage: boolean;
}) => {
	const [contentType] = useContentType();
	const data = useLazyLoadQuery<CollaboratorsQueryType>(CollaboratorsQuery, variables, {
		fetchPolicy: 'store-or-network',
		fetchKey,
	});
	// Keep the demo polling fallback; live docs otherwise refresh after collaboration activity.
	const pollingEnabled = isLivePage && fg('team_eu_26_demo_temp_contributors_polling');
	const { error, refreshNow } = useContributorsPolling(refetch, {
		pollingEnabled,
	});
	const eventRefreshEnabled = isLivePage && !pollingEnabled;
	const onActivity = useContributorsActivityRefresh({
		contentId: variables.id,
		enabled: eventRefreshEnabled,
		refreshNow,
	});
	useLiveContributorsActivity({
		enabled: eventRefreshEnabled,
		cloudId: variables.cloudId,
		contentId: variables.id,
		contentType,
		onActivity,
	});
	const onPageUpdated = useCallback(
		(_eventName: string, response: ConfluenceOnContentModified) => {
			if (response.id === variables.id && response.pageUpdated?.trigger === 'publish') {
				// Classic contributors change on publish, regardless of who published the page.
				void refreshNow({ queueIfInFlight: true });
			}
		},
		[variables.id, refreshNow],
	);
	useSubscriptionEvent({
		contentId: variables.id,
		contentType,
		delta: 'pageUpdated',
		subscriberId: 'bylineContributorsPageUpdated',
		onDataReceived: onPageUpdated,
		skip: isLivePage || (contentType !== 'page' && contentType !== 'blogpost'),
	});
	useEffect(() => {
		if (isDropdownOpen) {
			// Opening the popup refreshes both views and restarts the interval when polling is enabled.
			void refreshNow();
		}
	}, [isDropdownOpen, refreshNow]);
	const value = useMemo(
		() => ({ data, loading: false, error, refetch: refreshNow }),
		[data, error, refreshNow],
	);

	return (
		<CollaboratorsQueryContext.Provider value={value}>
			{error && <ErrorDisplay error={error} />}
			{children}
		</CollaboratorsQueryContext.Provider>
	);
};
